import { getIdTokenPayload } from '$shared/auth/auth-service';
import { buildUserId } from '$shared/auth/build-user-id';
import { queryClient } from '$shared/query/query-client';
import { retryFunc } from '$shared/utils/retry-func';
import {
  getSubscriptionDisplayName,
  getSubscriptionTypeDisplayName,
} from '$shared/utils/subscription.util';

import { getSubscriptionSync } from '$features/subscription/subscription-api';
import { createGetUserQuery } from '$features/users/users.api';

const performanceCookiesGroupName = 'C0002';

// Store reference to the click handler for cleanup
let clickAwayHandler: ((event: MouseEvent) => void) | null = null;

function setupClickAwayListener() {
  // Clean up existing listener first
  if (clickAwayHandler) {
    document.removeEventListener('click', clickAwayHandler, true);
  }

  // Official Pendo-style click-away handler based on Pendo snippets
  clickAwayHandler = (event: MouseEvent) => {
    const target = event.target as Element;
    const isClickInsideResourceCenter = target.closest(
      '#pendo-resource-center-container, .pendo-resource-center',
    );
    const hasVisibleResourceCenter = document.querySelector(
      '#pendo-resource-center-container:not([style*="display: none"])',
    );
    // Check if clicking on Pendo launcher while Resource Center is open
    const isLauncherClick = target.closest(
      '._pendo-badge-image, [id*="pendo-image-badge"]',
    );

    // Dismiss Resource Center if click is outside (but not if clicking launcher - let Pendo handle that)
    if (
      !isClickInsideResourceCenter &&
      !isLauncherClick &&
      hasVisibleResourceCenter &&
      window.pendo
    ) {
      // Use official Pendo Resource Center API with safe access -- https://github.com/pendo-io/snippets/blob/master/resourceCenter/dismissRCWhenClickOutsideTargetElementRC/dismissRCWhenClickOutsideTargetElementRC_v2.js
      window.pendo.BuildingBlocks?.BuildingBlockResourceCenter?.dismissResourceCenter?.();
    }
  };

  // Add event listener
  document.addEventListener('click', clickAwayHandler, true);
}
export interface PendoMetadata {
  userRole?: string;
  subscription?: string;
  subscriptionType?: string;
}

function initializeTracking(
  accountId: string,
  visitorId: string,
  email: string,
  version: string,
  metadata?: PendoMetadata,
) {
  if (!window.pendo) {
    // eslint-disable-next-line no-console -- for debugging purposes
    console.warn('could not find pendo instance for tracking');
    return;
  }

  window.pendo.initialize({
    disableGuides: false,
    visitor: {
      id: visitorId, // Required if user is logged in
      email, // Recommended if using Pendo Feedback, or NPS Email
      ...(metadata?.userRole ? { user_role: metadata.userRole } : {}),
    },
    account: {
      id: accountId, // Highly recommended, required if using Pendo Feedback
      'vnls-version': version,
      ...(metadata?.subscription ? { subscription: metadata.subscription } : {}),
      ...(metadata?.subscriptionType
        ? { subscription_type: metadata.subscriptionType }
        : {}),
    },
  });

  // Setup click-away behavior using official Pendo patterns
  setupClickAwayListener();
}

function teardownPendo() {
  if (!window.pendo) {
    // eslint-disable-next-line no-console -- for debugging purposes
    console.warn('could not find pendo instance for tracking');
    return;
  }

  window.pendo.teardown();

  // Clean up click-away listener
  if (clickAwayHandler) {
    document.removeEventListener('click', clickAwayHandler, true);
    clickAwayHandler = null;
  }

  // Clean up Pendo localStorage
  Object.keys(localStorage)
    .filter((key) => key.includes('_pendo_'))
    .forEach((key) => localStorage.removeItem(key));
}

const COOKIE_CATEGORIES = {
  PERFORMANCE: 'Performance Cookies',
};

export const initializePendo = async () => {
  const payload = await getIdTokenPayload();
  if (payload) {
    const [userResult, subscriptionResult] = await Promise.allSettled([
      buildUserId(payload.email, payload.tenantId).then((userId) =>
        userId ? queryClient.fetchQuery(createGetUserQuery(userId)) : undefined,
      ),
      getSubscriptionSync(queryClient),
    ]);

    const user = userResult.status === 'fulfilled' ? userResult.value : undefined;
    const subscriptionData =
      subscriptionResult.status === 'fulfilled' ? subscriptionResult.value : undefined;

    const metadata: PendoMetadata = {
      userRole: user?.roles?.[0],
      subscription: getSubscriptionDisplayName(subscriptionData?.subscription),
      subscriptionType: getSubscriptionTypeDisplayName(
        subscriptionData?.subscriptionType,
      ),
    };

    initializeTracking(payload.tenantId, payload.sub, payload.email, '4.0.0', metadata);
  }
};

export async function checkPreferencesAndInitializePendo() {
  const isPendoEnabled = window.OnetrustActiveGroups?.split(',')?.includes(
    performanceCookiesGroupName,
  );
  if (isPendoEnabled) {
    await initializePendo();
  }
}

export function checkOneTrustAndInitializePendo() {
  if (!import.meta.env.PUBLIC_ONE_TRUST_ID) {
    return;
  }
  retryFunc(
    async () => {
      if (!window.OneTrust) {
        throw new Error('please retry');
      }
      window.OneTrust.OnConsentChanged((state) => {
        const performanceGroup = window.OneTrust.GetDomainData().Groups.find(
          (group) => group.GroupName === COOKIE_CATEGORIES.PERFORMANCE,
        );
        if (state.detail.includes(performanceGroup?.CustomGroupId)) {
          initializePendo();
        } else {
          teardownPendo();
        }
      });

      return 'all good, no more retries';
    },
    100,
    100,
  );
}
